import type { Asset, CompanyStatus } from '../lib/types.ts';

// Compliance roll-up for the dashboard health panels. Companies and drivers
// share CompanyStatus, whose labels (companyStatus.ts) already read `compliant`
// as "Compliant" and `expired` as "Not Compliant"; the remaining active states
// are work-in-progress. `deactivated` records are parked and excluded entirely,
// mirroring the companies list (whose default view hides them).

export type StatusBuckets = {
  compliant: number;
  notCompliant: number;
  inProgress: number;
  total: number;
};

const IN_PROGRESS: ReadonlySet<CompanyStatus> = new Set<CompanyStatus>([
  'invited',
  'in_progress',
  'review',
  'changes',
]);

// A status is "in progress" when the entity is mid-workflow: invited but not
// started, actively filling out, submitted for review, or reworking requested
// changes. Shared by the health-panel "In progress" segment and the driver work
// queue so the tile counts and the queue rows classify identically.
export function isInProgress(status: CompanyStatus): boolean {
  return IN_PROGRESS.has(status);
}

export function statusBuckets(statuses: CompanyStatus[]): StatusBuckets {
  const buckets: StatusBuckets = { compliant: 0, notCompliant: 0, inProgress: 0, total: 0 };
  for (const status of statuses) {
    if (status === 'compliant') buckets.compliant += 1;
    else if (status === 'expired') buckets.notCompliant += 1;
    else if (isInProgress(status)) buckets.inProgress += 1;
    else continue; // deactivated — parked, not counted
    buckets.total += 1;
  }
  return buckets;
}

export type AssetBuckets = {
  compliant: number;
  notCompliant: number;
  total: number;
};

// Assets carry a true binary compliance_status. Inactive assets are parked, so
// only active assets contribute to the panel (mirrors the deactivated rule).
export function assetBuckets(assets: Pick<Asset, 'status' | 'compliance_status'>[]): AssetBuckets {
  const buckets: AssetBuckets = { compliant: 0, notCompliant: 0, total: 0 };
  for (const asset of assets) {
    if (asset.status !== 'active') continue;
    if (asset.compliance_status === 'compliant') buckets.compliant += 1;
    else buckets.notCompliant += 1;
    buckets.total += 1;
  }
  return buckets;
}

// Percentage of `total` represented by `part`, rounded to one decimal place for
// stable segment widths. Guards against a zero total (empty panel → 0%).
export function pct(part: number, total: number): number {
  if (total <= 0) return 0;
  return Math.round((part / total) * 1000) / 10;
}
